/**
 * @component NaverMap
 * @description 네이버 지도 SDK를 기반으로 지점 마커와 위치 정보를 시각화하는 지도 컴포넌트입니다.
 * @usage LocationSection에서 지점 선택 상태와 연동되는 지도 영역으로 사용됩니다.
 */

import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const MapContainer = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: rgba(26, 26, 46, 0.06);
`;

const MapError = styled.p`
  margin: 10px 0 0;
  color: #c0392b;
  font-size: 0.92rem;
`;

export default function NaverMap({ branches, selectedBranchId, onMapReady }) {
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const infowindowsRef = useRef([]);
  const initializedRef = useRef(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!mapNodeRef.current || initializedRef.current) {
      return undefined;
    }

    if (!window.naver?.maps) {
      // [에러 처리]: SDK 미로드 상태를 사용자 메시지로 안내합니다.
      setLoadError('네이버 지도 SDK가 로드되지 않았습니다. public/index.html의 script 태그를 확인해주세요.');
      return undefined;
    }

    const targetBranch = branches.find((branch) => branch.id === selectedBranchId) ?? branches[0];
    if (!targetBranch) {
      return undefined;
    }

    initializedRef.current = true;
    setLoadError('');

    // [초기화]: 선택 지점을 중심으로 지도 인스턴스를 생성합니다.
    const map = new window.naver.maps.Map(mapNodeRef.current, {
      center: new window.naver.maps.LatLng(targetBranch.lat, targetBranch.lng),
      zoom: 16,
    });

    mapRef.current = map;
    // [상태 전달]: 상위 컴포넌트에서 지도 중심 이동을 제어할 수 있도록 map 객체를 전달합니다.
    onMapReady?.(map);

    markersRef.current = branches.map((branch) => {
      const marker = new window.naver.maps.Marker({
        position: new window.naver.maps.LatLng(branch.lat, branch.lng),
        map,
      });

      const infoWindow = new window.naver.maps.InfoWindow({
        content: `<div style="padding:8px 12px;font-size:13px;font-weight:600;white-space:nowrap;">${branch.name}</div>`,
      });

      infoWindow.open(map, marker);
      infowindowsRef.current.push(infoWindow);

      return marker;
    });

    return () => {
      // [정리 작업]: 언마운트 시 마커/인포윈도우 및 map 참조를 제거합니다.
      infowindowsRef.current.forEach((infoWindow) => infoWindow.close());
      markersRef.current.forEach((marker) => marker.setMap(null));
      infowindowsRef.current = [];
      markersRef.current = [];
      mapRef.current = null;
      initializedRef.current = false;
      onMapReady?.(null);
    };
  }, [branches, onMapReady, selectedBranchId]);

  useEffect(() => {
    if (!mapRef.current || !window.naver?.maps) {
      return;
    }

    const targetBranch = branches.find((branch) => branch.id === selectedBranchId);
    if (!targetBranch) {
      return;
    }

    // [상태 동기화]: 선택 지점이 변경되면 지도 중심 좌표를 업데이트합니다.
    mapRef.current.setCenter(new window.naver.maps.LatLng(targetBranch.lat, targetBranch.lng));
  }, [branches, selectedBranchId]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MapContainer ref={mapNodeRef} />
      {loadError ? <MapError>{loadError}</MapError> : null}
    </div>
  );
}
